import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LayoutDashboard } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const LOGO = "/logo-badge.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/past-jobs", label: "Past Jobs" },
  { to: "/guide", label: "Guide" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/qa", label: "Q&A" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-navbar">
      <div className={`bg-[#23282B] border-b border-white/10 transition-shadow ${scrolled ? "shadow-[0_8px_24px_rgba(0,0,0,0.35)]" : ""}`}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 flex items-center justify-between h-20 md:h-24">

          {/* Logo lockup — badge + typographic wordmark */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img
              src={LOGO}
              alt="Amazing View Contracting"
              className="h-14 md:h-[4.5rem] w-auto block"
            />
            <span className="block leading-none">
              <span className="block font-display text-white text-xl sm:text-2xl md:text-4xl uppercase tracking-[0.04em] leading-none">Amazing View</span>
              <span className="block text-[#9CC4DB] text-[10px] sm:text-[11px] md:text-sm font-bold tracking-[0.4em] uppercase mt-1">Contracting</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-[13px] font-semibold tracking-[0.14em] uppercase transition-colors border-b-2 ${
                    isActive ? "text-[#9CC4DB] border-[#9CC4DB]" : "text-white/80 border-transparent hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <AuthCta user={user} />
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 text-white"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link
              to="/contact"
              data-testid="nav-mobile-quote-cta"
              className="md:hidden inline-flex items-center whitespace-nowrap bg-[#2F5F80] active:scale-95 text-white text-[11px] font-bold tracking-[0.12em] uppercase px-3 py-2.5 transition-transform"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </div>

      {open && <MobileMenu user={user} />}
    </header>
  );
}

function AuthCta({ user }) {
  const signedIn = user && user !== false;
  return (
    <Link
      to={signedIn ? "/dashboard" : "/login"}
      data-testid={signedIn ? "nav-dashboard-cta" : "nav-login-cta"}
      className="hidden md:inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-[13px] font-bold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors"
    >
      {signedIn ? <><LayoutDashboard size={16} /> Dashboard</> : <><LogIn size={16} /> Employee Login</>}
    </Link>
  );
}

function MobileMenu({ user }) {
  const signedIn = user && user !== false;
  return (
    <div className="lg:hidden bg-[#23282B] border-t border-white/10" data-testid="mobile-menu">
      <nav className="px-5 pt-3 pb-4 flex flex-col">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            className={({ isActive }) =>
              `py-3 px-2 text-sm font-semibold uppercase tracking-[0.18em] border-l-2 ${
                isActive ? "text-[#9CC4DB] border-[#9CC4DB]" : "text-white border-transparent"
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
        <Link
          to={signedIn ? "/dashboard" : "/login"}
          data-testid="mobile-login-cta"
          className="mt-3 inline-flex items-center justify-center gap-2 bg-[#2F5F80] text-white text-[13px] font-bold uppercase tracking-[0.14em] px-5 py-3"
        >
          {signedIn ? <><LayoutDashboard size={16}/> Dashboard</> : <><LogIn size={16}/> Employee Login</>}
        </Link>
      </nav>
    </div>
  );
}
